// React
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import { Card, Fade, Grid, Icon, Modal, Tooltip } from "@mui/material";

import { gql, useLazyQuery, useMutation } from "@apollo/client";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import ModalDelete from "components/Modals/Delete";
import CustomPagination from "components/Pagination";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import DataTable from "components/Tables/DataTable";
import { MessageManager } from "context";
import { MenuContext } from "context/menuContext";
import dayjs from "dayjs";
import { getUproColor } from "utils/colors";
import * as XLSX from "xlsx";
import dataCuestionario from "../../data/dataCuestionarios";
import ModalCuestionario from "../ModalCuestionario";
import ModalCuestionarioReporte from "../ModalCuestionarioReporte";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "70%", xxl: "60%" },
  overflowY: "auto",
};

export default function ModalCuestionarios({ open, handleClose }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openModalCuestionarioReporte, setOpenModalCuestionarioReporte] = useState(false);
  const [selectedCuestionario, setSelectedCuestionario] = useState(null);
  const [page, setPage] = useState(1);
  const [pagesInfo, setPagesInfo] = useState({
    pages: 0,
    total: 0,
  });
  const [orderBy, setOrderBy] = useState({
    campo: "nombre",
    orden: "ASC",
  });
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const { menu } = useContext(MenuContext);

  const { handleSnackbar } = useContext(MessageManager);
  const { columns, getRows } = dataCuestionario;

  const handleOrderBy = (campo) => {
    if (orderBy.campo === campo) {
      if (orderBy.orden === "ASC") {
        setOrderBy({
          campo: campo,
          orden: "DESC",
        });
      } else {
        setOrderBy({
          campo: campo,
          orden: "ASC",
        });
      }
    } else {
      setOrderBy({
        campo: campo,
        orden: "ASC",
      });
    }
  };

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      setSearch(event.target.value);
      setPage(1);
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const [deleteCuestionario, { loading: loadingDelete }] = useMutation(
    gql`
      mutation deleteCuestionario($id: ID!) {
        deleteCuestionario(id: $id) {
          id
        }
      }
    `
  );

  const [getCuestionarios, { loading, error, data, refetch }] = useLazyQuery(
    gql`
      query getCuestionarios($search: String, $page: Int, $order: Order) {
        paginationInfo {
          pages
          total
        }
        cuestionarios(search: $search, pagination: { page: $page, limit: 10 }, order: $order) {
          id
          nombre
          fechaLimite
          obligatorio
          cantidadRespuestas
        }
      }
    `,
    {
      variables: {
        search: search || null,
        page: page,
        order: { field: orderBy.campo, order: orderBy.orden },
      },
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  const [getCuestionariosRespuestas, { loading: loadingCuestionariosRespuestas }] = useLazyQuery(
    gql`
      query getCuestionariosRespuestas($id: ID!) {
        cuestionario(id: $id) {
          id
          nombre
          preguntas {
            id
            titulo
            tipo
            opciones
          }
          respuestas
        }
      }
    `,
    {
      fetchPolicy: "no-cache",
      cachePolicy: "no-cache",
    }
  );

  useEffect(() => {
    if (open) {
      getCuestionarios();
    }
  }, [open]);

  const handleDeleteCuestionario = async (id) => {
    try {
      await deleteCuestionario({ variables: { id } });
      refetch();
      setSelectedCuestionario();
      handleSnackbar("Cuestionario eliminado correctamente", "success");
    } catch (e) {
      handleSnackbar(e.message || "Error al eliminar el cuestionario", "error");
      refetch();
      setSelectedCuestionario();
    }
  };

  useEffect(() => {
    if (data) {
      setRows(
        getRows(data.cuestionarios, (event, item) =>
          menu({
            open: event.currentTarget,
            align: "right",
            options: [
              {
                name: "Editar",
                icon: {
                  icon: "edit",
                  color: getUproColor("uproGreen"),
                },
                onClick: () => {
                  setSelectedCuestionario(item);
                  setOpenModalAdd(true);
                },
              },
              {
                name: "Eliminar",
                icon: {
                  icon: "delete",
                  color: getUproColor("error"),
                },
                onClick: () => {
                  setSelectedCuestionario(item);
                  setOpenModalDelete(true);
                },
              },
              {
                name: "Ver reportes",
                icon: {
                  icon: "bar_chart",
                  color: getUproColor("uproYellow"),
                },
                onClick: () => {
                  setSelectedCuestionario(item);
                  setOpenModalCuestionarioReporte(true);
                },
              },
              {
                name: "Descargar respuestas",
                icon: {
                  icon: "download",
                  color: getUproColor("uproBlue"),
                },
                onClick: () => {
                  setSelectedCuestionario(item);
                  getCuestionariosRespuestas({ variables: { id: item.id } })
                    .then((res) => {
                      let reporte = res.data.cuestionario.respuestas || [];
                      const preguntas = res.data.cuestionario.preguntas || [];
                      let reporteExportar = reporte.map((item) => {
                        const respuestas = item.respuestas || [];
                        const columnasPreguntas = preguntas.reduce((acc, pregunta) => {
                          acc[pregunta.titulo] = respuestas.find(
                            (respuesta) => respuesta.idPregunta == pregunta.id
                          )?.respuesta;
                          return acc;
                        }, {});

                        return {
                          Alumno: `${item.usuario.apellido} ${item.usuario.nombre}`,
                          DNI: item.usuario.dni,
                          "Sede del curso": item.usuario.sedeCurso.nombre,
                          "Sede de la carrera": item.usuario.sedeCarrera.nombre,
                          ...columnasPreguntas,
                        };
                      });

                      const cabecera = [[`Respuestas de ${res.data.cuestionario.nombre}`]];

                      const sheet = XLSX.utils.aoa_to_sheet(cabecera);

                      XLSX.utils.sheet_add_json(sheet, reporteExportar, {
                        skipHeader: false,
                        origin: -1,
                      });

                      sheet["!merges"] = [
                        { s: { r: 0, c: 0 }, e: { r: 0, c: preguntas.length + 1 } },
                      ];

                      const workbook = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(workbook, sheet, `Respuestas`);

                      XLSX.writeFile(
                        workbook,
                        `Respuestas de ${res.data.cuestionario.nombre} - ${dayjs().format(
                          "DD-MM-YYYY"
                        )}.xlsx`
                      );
                    })
                    .catch((e) => {
                      handleSnackbar(e.message || "Error al descargar las respuestas", "error");
                    });
                },
              },
            ],
            handleClose: () => {},
          })
        )
      );
      setPagesInfo(data.paginationInfo);
    }
  }, [data]);
  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <Card>
              <Grid container spacing={2} p={3}>
                <Grid item xs={12}>
                  <SoftBox
                    display={{
                      xs: "flex-row",
                      sm: "flex",
                    }}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <SoftBox display="flex" justifyContent="space-between" alignItems="center">
                      <SoftTypography variant="h6">Cuestionarios</SoftTypography>
                      <SoftBox
                        display={{
                          xs: "flex",
                          sm: "none",
                        }}
                        alignItems="center"
                        gap={1}
                      >
                        <Tooltip title="Agregar nuevo cuestionario" placement="top">
                          <SoftButton
                            color="primary"
                            circular
                            iconOnly
                            onClick={() => {
                              setOpenModalAdd(true);
                            }}
                          >
                            <Icon>add</Icon>
                          </SoftButton>
                        </Tooltip>
                        <Tooltip title="Cerrar" placement="top">
                          <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                            close
                          </Icon>
                        </Tooltip>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox
                      display="flex"
                      alignItems="center"
                      justifyContent="end"
                      mt={{
                        xs: 2,
                        sm: 0,
                      }}
                    >
                      <SoftBox
                        mr={{
                          xs: 0,
                          sm: 2,
                        }}
                        sx={{
                          width: {
                            xs: "100%",
                            sm: "auto",
                          },
                        }}
                      >
                        <SoftInput
                          placeholder="Escribe aquí..."
                          icon={{ component: "search", direction: "left" }}
                          onChange={handleSearch}
                        />
                      </SoftBox>
                      <SoftBox
                        display={{
                          xs: "none",
                          sm: "block",
                        }}
                      >
                        <Tooltip title="Agregar nuevo cuestionario" placement="top">
                          <SoftButton
                            color="primary"
                            circular
                            onClick={() => setOpenModalAdd(true)}
                          >
                            <Icon>add</Icon>
                            &nbsp;Agregar
                          </SoftButton>
                        </Tooltip>
                      </SoftBox>
                      <SoftBox
                        ml={2}
                        display={{
                          xs: "none",
                          sm: "flex",
                        }}
                      >
                        <Tooltip title="Cerrar" placement="top">
                          <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                            close
                          </Icon>
                        </Tooltip>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                </Grid>
              </Grid>
              <SoftBox
                sx={{
                  "& .MuiTableRow-root:not(:last-child)": {
                    "& td": {
                      borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                        `${borderWidth[1]} solid ${borderColor}`,
                    },
                  },
                }}
              >
                {loading || loadingCuestionariosRespuestas ? (
                  <Loading />
                ) : rows?.length > 0 ? (
                  <DataTable
                    columns={columns}
                    rows={rows}
                    order={orderBy}
                    setOrder={handleOrderBy}
                  />
                ) : (
                  <SinDatos />
                )}
              </SoftBox>
              <CustomPagination
                loading={loading}
                length={data?.cuestionarios.length}
                total={pagesInfo?.total}
                pages={pagesInfo?.pages}
                page={page}
                setPage={setPage}
                noCard
              />
            </Card>
            <ModalCuestionario
              open={openModalAdd}
              handleClose={() => {
                setOpenModalAdd(false);
                setSelectedCuestionario(null);
              }}
              refetch={refetch}
              selectedCuestionario={selectedCuestionario}
            />
            <ModalDelete
              open={openModalDelete}
              handleClose={() => setOpenModalDelete(false)}
              nombre={`el cuestionario ${selectedCuestionario?.nombre}`}
              functionDelete={() => {
                handleDeleteCuestionario(selectedCuestionario.id);
                setOpenModalDelete(false);
              }}
            />
            <ModalCuestionarioReporte
              open={openModalCuestionarioReporte}
              handleClose={() => setOpenModalCuestionarioReporte(false)}
              selectedCuestionario={selectedCuestionario}
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalCuestionarios.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
};
